import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'
import { heroFacts } from '@/constants/estate'

/*
   Hero
   Full-viewport photograph with layered gradients for legibility. The
   headline and calls to action are anchored bottom-left, and the four
   headline facts run along the base like a quiet specification strip.
*/
export const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-heading' className='relative isolate flex min-h-[100svh] flex-col overflow-hidden bg-charcoal text-cream'>
            {/* Photograph, settling slowly into place on load */}
            <div className='absolute inset-0 -z-10'>
                <Image
                    src='/images/hero.jpg'
                    alt='A single-storey Rockwood home at golden hour, with a level lawn and lush indigenous garden'
                    fill
                    priority
                    sizes='100vw'
                    className='object-cover object-[68%_center] motion-safe:animate-hero-settle'
                />
            </div>

            {/* Legibility gradients: top for the header, left and bottom for the copy */}
            <div aria-hidden='true' className='absolute inset-x-0 top-0 -z-10 h-44 bg-gradient-to-b from-charcoal/55 to-transparent' />
            <div aria-hidden='true' className='absolute inset-0 -z-10 bg-gradient-to-r from-charcoal/75 via-charcoal/30 to-transparent' />
            <div aria-hidden='true' className='absolute inset-x-0 bottom-0 -z-10 h-3/4 bg-gradient-to-t from-charcoal/85 via-charcoal/35 to-transparent' />

            <div className='mx-auto flex w-full max-w-7xl flex-1 flex-col justify-end px-6 pb-8 pt-36 lg:px-10 lg:pb-12'>
                <div className='max-w-3xl'>
                    <p className='inline-flex items-center gap-3 text-[0.6875rem] font-medium uppercase tracking-[0.32em] text-cream/80 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-1000'>
                        <span aria-hidden='true' className='h-px w-8 bg-current opacity-60' />
                        Hillcrest · KwaZulu-Natal
                    </p>

                    <h1
                        id='hero-heading'
                        className='mt-6 font-heading text-[2.75rem] leading-[1.04] tracking-[-0.01em] sm:text-6xl lg:text-[5.25rem] motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-1000'
                    >
                        Fourteen homes.
                        <br />
                        One <em className='italic text-cream/90'>quiet</em> address.
                    </h1>

                    <p className='mt-7 max-w-xl text-base leading-relaxed text-cream/80 sm:text-lg motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-1000 motion-safe:delay-150 motion-safe:fill-mode-both'>
                        Rockwood Estate is a private, gated enclave of freestanding, single-storey homes in central Hillcrest. Three bedrooms, a level garden and a fireplace in every home, for a calmer way to live.
                    </p>

                    <div className='mt-10 flex flex-col gap-3 sm:flex-row motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-1000 motion-safe:delay-300 motion-safe:fill-mode-both'>
                        <ButtonLink href='#residences' variant='light' size='lg'>
                            Explore the residences
                            <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/button:translate-x-0.5' />
                        </ButtonLink>

                        <ButtonLink href='#enquire' variant='outline-light' size='lg'>
                            Book a private viewing
                        </ButtonLink>
                    </div>
                </div>

                {/* Specification strip */}
                <dl className='mt-16 grid grid-cols-2 border-t border-cream/20 sm:grid-cols-4 lg:mt-20'>
                    {heroFacts.map((fact, index) => (
                        <div
                            key={fact.label}
                            className={cn(
                                'flex items-baseline gap-3 py-5 sm:py-6',
                                index % 2 === 1 && 'border-l border-cream/20 pl-5',
                                index >= 2 && 'border-t border-cream/20 sm:border-t-0',
                                index > 0 && 'sm:border-l sm:border-cream/20 sm:pl-6'
                            )}
                        >
                            <dt className='order-2 text-[0.6875rem] uppercase leading-snug tracking-[0.18em] text-cream/70'>
                                {fact.label}
                            </dt>

                            <dd className='order-1 font-heading text-3xl sm:text-4xl'>
                                {fact.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>

            {/* Scroll cue, shown on wide screens only */}
            <div aria-hidden='true' className='absolute right-8 top-1/2 hidden -translate-y-1/2 flex-col items-center gap-4 xl:flex'>
                <span className='text-[0.625rem] uppercase tracking-[0.32em] text-cream/60 [writing-mode:vertical-rl]'>
                    Scroll
                </span>

                <span className='relative h-16 w-px overflow-hidden bg-cream/20'>
                    <span className='absolute inset-0 bg-cream motion-safe:animate-scroll-cue' />
                </span>
            </div>
        </section>
    )
}
